import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

// Los archivos ESM de @ffmpeg/core viven en /public/ffmpeg/ (copiados por el
// script `postinstall` desde node_modules). Servirlos same-origin evita
// dependencias de CDNs que ad-blockers como Brave Shields o uBlock bloquean.
const ffmpegAssetBaseURL = `${import.meta.env.BASE_URL}ffmpeg/`;
const coreURL = `${ffmpegAssetBaseURL}ffmpeg-core.js`;
const wasmURL = `${ffmpegAssetBaseURL}ffmpeg-core.wasm`;

/**
 * Tamaño en bytes a partir del cual chunkeamos.
 * OpenRouter Whisper acepta archivos hasta ~25 MB. Dejamos margen.
 */
export const CHUNK_THRESHOLD_BYTES = 24 * 1024 * 1024;

/**
 * Duración de cada chunk en segundos. Chunks cortos reducen timeouts del
 * proveedor STT y hacen que Convex procese cada job rápido.
 */
const CHUNK_DURATION_SEC = 300;

/**
 * Bitrate del re-encode. 32kbps mono es suficiente para voz inteligible
 * y reduce dramáticamente el tamaño total enviado al modelo.
 */
const CHUNK_BITRATE = '32k';

// Recorta pausas largas que suelen disparar alucinaciones de STT en inglés.
const STT_AUDIO_FILTER =
  'silenceremove=start_periods=1:start_duration=0.3:start_threshold=-50dB:stop_periods=-1:stop_duration=1.1:stop_threshold=-50dB';

let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoadPromise: Promise<FFmpeg> | null = null;

/**
 * Carga ffmpeg.wasm la primera vez. Las siguientes llamadas reutilizan la instancia.
 * Los archivos del core se sirven desde el mismo origen (bundle local de Vite),
 * pero los pasamos por toBlobURL para evitar restricciones extra de tipo/MIME.
 */
async function getFFmpeg(onLog?: (msg: string) => void): Promise<FFmpeg> {
  if (ffmpegInstance) return ffmpegInstance;
  if (ffmpegLoadPromise) return ffmpegLoadPromise;

  ffmpegLoadPromise = (async () => {
    try {
      const ff = new FFmpeg();
      if (onLog) {
        ff.on('log', ({ message }) => onLog(message));
      }
      await ff.load({
        coreURL: await toBlobURL(coreURL, 'text/javascript'),
        wasmURL: await toBlobURL(wasmURL, 'application/wasm'),
      });
      ffmpegInstance = ff;
      return ff;
    } catch (error) {
      ffmpegLoadPromise = null;
      throw error;
    }
  })();

  return ffmpegLoadPromise;
}

export interface AudioChunk {
  /** Datos del chunk listo para subir */
  blob: Blob;
  /** Nombre sintético, p. ej. "reunion_001.mp3" */
  fileName: string;
  /** Formato (extensión) — necesario para OpenRouter */
  format: string;
  /** Tamaño en bytes */
  sizeBytes: number;
  /** Índice 0-based del chunk dentro del audio original */
  index: number;
}

export interface ChunkProgress {
  stage: 'loading-ffmpeg' | 'splitting' | 'reading-chunks' | 'done';
  chunkIndex?: number;
  totalChunks?: number;
  message?: string;
}

export interface ChunkAudioOptions {
  /** Fuerza re-encode a MP3 aunque el archivo original sea pequeño. */
  forceMp3?: boolean;
  /** Duración objetivo de cada fragmento generado por ffmpeg. */
  chunkDurationSec?: number;
}

/**
 * Si el archivo es pequeño, devuelve un único "chunk" envolviendo el archivo original.
 * Si supera el umbral, o si el proveedor lo requiere, lo segmenta con
 * ffmpeg.wasm en chunks re-encodeados a mp3 mono 32kbps.
 */
export async function chunkAudioFile(
  file: File,
  onProgress?: (p: ChunkProgress) => void,
  onLog?: (msg: string) => void,
  durationSec?: number | null,
  options: ChunkAudioOptions = {}
): Promise<AudioChunk[]> {
  const chunkDurationSec = options.chunkDurationSec ?? CHUNK_DURATION_SEC;
  const shouldUseFFmpeg =
    options.forceMp3 === true ||
    file.size > CHUNK_THRESHOLD_BYTES ||
    (durationSec ?? 0) > chunkDurationSec;

  // Caso simple: cabe en una sola petición y no es largo.
  if (!shouldUseFFmpeg) {
    const ext = (file.name.toLowerCase().split('.').pop() || 'm4a').replace(/[^a-z0-9]/g, '');
    const format = inferOpenRouterFormat(ext, file.type);
    return [
      {
        blob: file,
        fileName: file.name,
        format,
        sizeBytes: file.size,
        index: 0,
      },
    ];
  }

  // Caso ffmpeg: normalización y/o chunking.
  onProgress?.({ stage: 'loading-ffmpeg', message: 'Cargando ffmpeg.wasm…' });
  const ff = await getFFmpeg(onLog);

  const inputName = `input.${file.name.toLowerCase().split('.').pop() || 'm4a'}`;
  const outputPattern = 'chunk_%03d.mp3';

  const willSplit = (durationSec ?? chunkDurationSec + 1) > chunkDurationSec;
  onProgress?.({
    stage: 'splitting',
    message: willSplit ? 'Cortando audio en fragmentos…' : 'Normalizando audio a MP3…',
  });

  await ff.writeFile(inputName, await fetchFile(file));

  // Comando: re-encodea a mp3 mono 32kbps y segmenta cada chunkDurationSec seg.
  // Re-encodear (en lugar de -c copy) garantiza que cada chunk sea reproducible
  // de forma independiente; -c copy con m4a a veces produce headers inválidos.
  const exitCode = await ff.exec([
    '-i',
    inputName,
    '-ac',
    '1',           // mono
    '-ar',
    '16000',       // 16kHz — Whisper internamente usa 16kHz
    '-af',
    STT_AUDIO_FILTER,
    '-c:a',
    'libmp3lame',
    '-b:a',
    CHUNK_BITRATE,
    '-f',
    'segment',
    '-segment_time',
    String(chunkDurationSec),
    '-reset_timestamps',
    '1',
    outputPattern,
  ]);

  if (exitCode !== 0) {
    throw new Error(`ffmpeg falló con código ${exitCode}. Revisa la consola para detalles.`);
  }

  // Limpiamos el input para liberar memoria del FS virtual
  try {
    await ff.deleteFile(inputName);
  } catch {
    // ignore
  }

  // Recolectamos los chunks generados
  onProgress?.({ stage: 'reading-chunks', message: 'Leyendo fragmentos generados…' });

  const chunks: AudioChunk[] = [];
  // ffmpeg numera secuencialmente. Vamos hasta que falle el read.
  for (let i = 0; i < 999; i++) {
    const fileName = `chunk_${String(i).padStart(3, '0')}.mp3`;
    let data: Uint8Array;
    try {
      const result = await ff.readFile(fileName);
      // readFile devuelve Uint8Array | string; al pedir binary nos da Uint8Array
      data = result as Uint8Array;
    } catch {
      break; // No hay más chunks
    }

    if (!data || data.byteLength === 0) break;

    // Copia el ArrayBuffer subyacente para evitar referencias al heap de ffmpeg
    const blob = new Blob([new Uint8Array(data)], { type: 'audio/mpeg' });
    chunks.push({
      blob,
      fileName,
      format: 'mp3',
      sizeBytes: blob.size,
      index: i,
    });

    // Liberamos la memoria del FS virtual
    try {
      await ff.deleteFile(fileName);
    } catch {
      // ignore
    }

    onProgress?.({
      stage: 'reading-chunks',
      chunkIndex: i + 1,
      totalChunks: undefined, // todavía no sabemos el total
    });
  }

  if (chunks.length === 0) {
    throw new Error(
      'ffmpeg no produjo ningún chunk. ¿El archivo está corrupto o tiene un formato no soportado?'
    );
  }

  onProgress?.({ stage: 'done', totalChunks: chunks.length });
  return chunks;
}

function inferOpenRouterFormat(ext: string, mimeType: string): string {
  if (['m4a', 'mp3', 'wav', 'flac', 'aac', 'ogg', 'aiff'].includes(ext)) return ext;
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('mpeg')) return 'mp3';
  if (mimeType.includes('wav')) return 'wav';
  return 'm4a';
}
